import { useEffect, useRef } from "react";
import { Avatar, Box, Stack, Typography } from "@mui/material";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { colors, skyTint } from "@utils/colors";
import type { SmAiMessage } from "@hooks/use-sm-ai-chat/types";
import { MarkdownMessage } from "./markdown-message";

export const MessageList = ({
  messages,
  isStreaming,
  open,
  onInternalLinkClick,
}: {
  messages: SmAiMessage[];
  isStreaming: boolean;
  open: boolean;
  onInternalLinkClick?: () => void;
}) => {
  const bottomRef = useRef<HTMLDivElement>(null);

  // Keep the latest message in view — on open, on history load, on new
  // messages, and as streamed deltas grow the last message.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages, open]);

  if (messages.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Ask SM AI a question about this site.
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5}>
      {messages.map((message, index) => {
        const isUser = message.role === "user";
        const isPendingAssistant =
          !isUser && isStreaming && index === messages.length - 1 && message.content === "";

        return (
          <Stack
            key={message.id}
            direction="row"
            spacing={1}
            alignSelf={isUser ? "flex-end" : "flex-start"}
            sx={{ maxWidth: "88%" }}
          >
            {!isUser && (
              <Avatar sx={{ width: 28, height: 28, bgcolor: skyTint(0.16), color: colors.navy }}>
                <AutoAwesomeOutlinedIcon fontSize="small" />
              </Avatar>
            )}
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 2,
                bgcolor: isUser
                  ? colors.blue
                  : (t) => (t.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(25, 52, 68, 0.05)"),
                color: isUser ? colors.surface : "text.primary",
              }}
            >
              {isUser || isPendingAssistant ? (
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {isPendingAssistant ? "…" : message.content}
                </Typography>
              ) : (
                <MarkdownMessage content={message.content} onInternalLinkClick={onInternalLinkClick} />
              )}
            </Box>
            {isUser && (
              <Avatar sx={{ width: 28, height: 28, bgcolor: skyTint(0.16), color: colors.navy }}>
                <PersonOutlineIcon fontSize="small" />
              </Avatar>
            )}
          </Stack>
        );
      })}
      <div ref={bottomRef} />
    </Stack>
  );
};
